import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { db } from '../lib/db'
import { auth } from '../lib/auth'
import { 
  Store, UtensilsCrossed, Plus, ArrowLeft, Home, LogOut,
  Building2, MapPin, Phone, DollarSign, Clock, Tag, Image as ImageIcon,
  Save, Trash2, Edit2, Eye, EyeOff, CheckCircle2, AlertCircle, X
} from 'lucide-react'

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -50 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -50 }}
    className={`fixed top-4 right-4 z-50 ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    } text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2`}
  >
    {type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
    <span>{message}</span>
    <button onClick={onClose} className="hover:opacity-75">
      <X size={18} />
    </button>
  </motion.div>
)

const AdminLogin = ({ onSuccess }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    
    try {
      const session = await auth.login(email, password)
      setLoading(false)
      onSuccess(session)
    } catch (err) {
      setLoading(false)
      setError(err.message || 'Invalid email or password.')
      setPassword('')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 w-full max-w-md shadow-2xl"
      >
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-green-500 rounded-full flex items-center justify-center mb-4">
            <UtensilsCrossed className="text-white" size={32} />
          </div>
          <h1 className="text-white text-2xl font-bold">Ami Bites Admin</h1>
          <p className="text-gray-400 text-sm mt-1">Restaurant & Menu Management</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError('') }}
              placeholder="Email"
              required
              className="w-full bg-gray-700/50 text-white placeholder-gray-400 border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors"
            />
          </div>
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError('') }}
              placeholder="Password"
              required
              className={`w-full bg-gray-700/50 text-white placeholder-gray-400 border ${error ? 'border-red-500' : 'border-gray-600'} rounded-lg px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors`}
            />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-all"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </motion.div>
    </div>
  )
}

export default function AdminPanel() {
  const [session, setSession] = useState(null)
  const [authChecked, setAuthChecked] = useState(false)
  const [restaurants, setRestaurants] = useState([])
  const [selectedRestaurant, setSelectedRestaurant] = useState(null)
  const [menuItems, setMenuItems] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState(null)
  const [editingItem, setEditingItem] = useState(null)
  const [showRestaurantForm, setShowRestaurantForm] = useState(false)
  const [editingRestaurant, setEditingRestaurant] = useState(null)
  
  const [restaurantForm, setRestaurantForm] = useState({
    name: '',
    description: '',
    cuisine_type: '',
    address: '',
    phone: '',
    image_url: '',
    logo_url: '',
    delivery_time: '',
    delivery_fee: '',
    minimum_order: '',
    is_active: true
  })
  
  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    price: '',
    category_name: '',
    image_url: '',
    preparation_time: '',
    allergens: '',
    dietary_tags: '',
    is_available: true,
    is_featured: false
  })

  useEffect(() => {
    const session = auth.getSession()
    setSession(session)
    setAuthChecked(true)
  }, [])

  useEffect(() => {
    if (session) loadRestaurants()
  }, [session])

  useEffect(() => {
    if (selectedRestaurant) loadMenuItems()
  }, [selectedRestaurant])

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  const loadRestaurants = async () => {
    try {
      const data = await db.getRestaurants()
      setRestaurants(data || [])
    } catch (error) {
      showToast('Failed to load restaurants', 'error')
    }
  }

  const loadMenuItems = async () => {
    try {
      setLoading(true)
      const items = await db.getMenuItems(selectedRestaurant.id)
      setMenuItems(items || [])

      const cats = await db.getMenuCategories(selectedRestaurant.id)
      setCategories(cats || [])
    } catch (error) {
      showToast('Failed to load menu items', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleRestaurantSubmit = async () => {
    if (!restaurantForm.name.trim()) {
      showToast('Restaurant name is required', 'error')
      return
    }

    try {
      setLoading(true)
      
      const dataToSubmit = {
        name: restaurantForm.name.trim(),
        description: restaurantForm.description.trim() || null,
        cuisine_type: restaurantForm.cuisine_type.trim() || null,
        address: restaurantForm.address.trim() || null,
        phone: restaurantForm.phone.trim() || null,
        image_url: restaurantForm.image_url.trim() || null,
        logo_url: restaurantForm.logo_url.trim() || null,
        delivery_time: restaurantForm.delivery_time.trim() || null,
        delivery_fee: restaurantForm.delivery_fee ? parseFloat(restaurantForm.delivery_fee) : null,
        minimum_order: restaurantForm.minimum_order ? parseFloat(restaurantForm.minimum_order) : null,
        is_active: restaurantForm.is_active
      }

      if (editingRestaurant) {
        await db.updateRestaurant(editingRestaurant.id, dataToSubmit)
        showToast('Restaurant updated successfully!')
      } else {
        const data = await db.createRestaurant(dataToSubmit)
        setSelectedRestaurant(data)
        showToast('Restaurant created successfully!')
      }
      
      await loadRestaurants()
      setShowRestaurantForm(false)
      setEditingRestaurant(null)
      setRestaurantForm({
        name: '',
        description: '',
        cuisine_type: '',
        address: '',
        phone: '',
        image_url: '',
        logo_url: '',
        delivery_time: '',
        delivery_fee: '',
        minimum_order: '',
        is_active: true
      })
    } catch (error) {
      showToast(`Error: ${error.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleItemSubmit = async () => {
    if (!itemForm.name.trim() || !itemForm.price) {
      showToast('Name and price are required', 'error')
      return
    }

    try {
      setLoading(true)
      let categoryId = null

      if (itemForm.category_name.trim()) {
        const cats = await db.getMenuCategories(selectedRestaurant.id)
        const existingCat = cats.find(c => c.name === itemForm.category_name.trim())

        if (existingCat) {
          categoryId = existingCat.id
        } else {
          const newCat = await db.createMenuCategory({ 
            restaurant_id: selectedRestaurant.id, 
            name: itemForm.category_name.trim(),
            is_active: true 
          })
          categoryId = newCat.id
        }
      }

      const allergenArray = itemForm.allergens
        ? itemForm.allergens.split(',').map(a => a.trim()).filter(a => a)
        : null

      const dietaryArray = itemForm.dietary_tags
        ? itemForm.dietary_tags.split(',').map(d => d.trim()).filter(d => d)
        : null

      const itemData = {
        restaurant_id: selectedRestaurant.id,
        category_id: categoryId,
        name: itemForm.name.trim(),
        description: itemForm.description.trim() || null,
        price: parseFloat(itemForm.price),
        image_url: itemForm.image_url.trim() || null,
        preparation_time: itemForm.preparation_time ? parseInt(itemForm.preparation_time) : null,
        allergens: allergenArray,
        dietary_tags: dietaryArray,
        is_available: itemForm.is_available,
        is_featured: itemForm.is_featured
      }

      if (editingItem) {
        await db.updateMenuItem(editingItem.id, itemData)
        showToast('Item updated successfully!')
      } else {
        await db.createMenuItem(itemData)
        showToast('Item added successfully!')
      }

      await loadMenuItems()
      setEditingItem(null)
      setItemForm({
        name: '',
        description: '',
        price: '',
        category_name: '',
        image_url: '',
        preparation_time: '',
        allergens: '',
        dietary_tags: '',
        is_available: true,
        is_featured: false
      })
    } catch (error) {
      showToast(`Error: ${error.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteItem = async (id) => {
    if (!confirm('Delete this item?')) return
    
    try {
      await db.deleteMenuItem(id)
      showToast('Item deleted!')
      await loadMenuItems()
    } catch (error) {
      showToast('Failed to delete item', 'error')
    }
  }

  const handleDeleteRestaurant = async (id) => {
    if (!confirm('Delete this restaurant and all its menu items?')) return
    
    try {
      await db.deleteRestaurant(id)
      showToast('Restaurant deleted!')
      setSelectedRestaurant(null)
      await loadRestaurants()
    } catch (error) {
      showToast('Failed to delete restaurant', 'error')
    }
  }

  const toggleAvailability = async (id, currentStatus) => {
    try {
      await db.toggleItemAvailability(id, !currentStatus)
      await loadMenuItems()
    } catch (error) {
      showToast('Failed to update item', 'error')
    }
  }

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    )
  }

  if (!session) {
    return <AdminLogin onSuccess={(s) => setSession(s)} />
  }

  const stats = {
    totalRestaurants: restaurants.length,
    activeRestaurants: restaurants.filter(r => r.is_active).length,
    totalItems: menuItems.length,
    activeItems: menuItems.filter(i => i.is_available).length,
    categories: categories.length
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-8 px-4">
      <AnimatePresence>
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                Ami Bites <span className="bg-gradient-to-r from-orange-500 to-green-500 bg-clip-text text-transparent">Admin Panel</span>
              </h1>
              <p className="text-gray-400">Complete restaurant and menu management</p>
            </div>
            <div className="flex gap-3">
              <a
                href="/"
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Home size={20} />
                <span className="hidden sm:inline">Back to Site</span>
              </a>
              <button
                onClick={() => { auth.logout(); window.location.reload(); }}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <LogOut size={20} />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Restaurants', value: stats.totalRestaurants, color: 'from-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' },
              { label: 'Active', value: stats.activeRestaurants, color: 'from-green-500/20', border: 'border-green-500/30', text: 'text-green-400' },
              { label: 'Menu Items', value: stats.totalItems, color: 'from-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400' },
              { label: 'Available', value: stats.activeItems, color: 'from-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-400' },
              { label: 'Categories', value: stats.categories, color: 'from-pink-500/20', border: 'border-pink-500/30', text: 'text-pink-400' }
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`bg-gradient-to-br ${stat.color} backdrop-blur-sm rounded-xl p-4 border ${stat.border}`}
              >
                <p className={`text-xs font-semibold uppercase ${stat.text} mb-1`}>{stat.label}</p>
                <p className="text-3xl font-bold text-white">{stat.value}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/10"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Store size={28} className="text-orange-500" />
              Restaurants
            </h2>
            <button
              onClick={() => {
                setShowRestaurantForm(!showRestaurantForm)
                if (!showRestaurantForm) {
                  setEditingRestaurant(null)
                  setRestaurantForm({
                    name: '',
                    description: '',
                    cuisine_type: '',
                    address: '',
                    phone: '',
                    image_url: '',
                    logo_url: '',
                    delivery_time: '',
                    delivery_fee: '',
                    minimum_order: '',
                    is_active: true
                  })
                }
              }}
              className="bg-gradient-to-r from-orange-500 to-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:from-orange-600 hover:to-green-600 transition-all"
            >
              <Plus size={20} />
              {showRestaurantForm ? 'Cancel' : 'Add Restaurant'}
            </button>
          </div>

          {showRestaurantForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white/5 rounded-xl p-6 mb-4 border border-white/10"
            >
              <h3 className="text-white font-semibold mb-4 text-lg">
                {editingRestaurant ? 'Edit Restaurant' : 'Add New Restaurant'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <Building2 size={16} className="text-orange-500" />
                    Restaurant Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., The Golden Spoon"
                    value={restaurantForm.name}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <Tag size={16} className="text-green-500" />
                    Cuisine Type
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Indian, Italian"
                    value={restaurantForm.cuisine_type}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, cuisine_type: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <MapPin size={16} className="text-blue-500" />
                    Address
                  </label>
                  <input
                    type="text"
                    placeholder="Street address, city"
                    value={restaurantForm.address}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, address: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <Phone size={16} className="text-purple-500" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={restaurantForm.phone}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, phone: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <Clock size={16} className="text-yellow-500" />
                    Delivery Time
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 30-45 min"
                    value={restaurantForm.delivery_time}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, delivery_time: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <DollarSign size={16} className="text-green-500" />
                    Delivery Fee
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g., 2.99"
                    value={restaurantForm.delivery_fee}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, delivery_fee: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <DollarSign size={16} className="text-green-500" />
                    Minimum Order
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g., 10.00"
                    value={restaurantForm.minimum_order}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, minimum_order: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <ImageIcon size={16} className="text-pink-500" />
                    Restaurant Image URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/restaurant-image.jpg"
                    value={restaurantForm.image_url}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, image_url: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <ImageIcon size={16} className="text-pink-500" />
                    Logo URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/logo.png"
                    value={restaurantForm.logo_url}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, logo_url: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-semibold mb-2">Description</label>
                  <textarea
                    placeholder="Tell customers about this restaurant..."
                    value={restaurantForm.description}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, description: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                    rows="3"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={restaurantForm.is_active}
                      onChange={(e) => setRestaurantForm({ ...restaurantForm, is_active: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-white font-semibold">Restaurant is Active (visible to customers)</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleRestaurantSubmit}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
                >
                  <Save size={18} />
                  {loading ? 'Saving...' : editingRestaurant ? 'Update Restaurant' : 'Create Restaurant'}
                </button>
                <button
                  onClick={() => {
                    setShowRestaurantForm(false)
                    setEditingRestaurant(null)
                    setRestaurantForm({
                      name: '',
                      description: '',
                      cuisine_type: '',
                      address: '',
                      phone: '',
                      image_url: '',
                      logo_url: '',
                      delivery_time: '',
                      delivery_fee: '',
                      minimum_order: '',
                      is_active: true
                    })
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {restaurants.map((restaurant) => (
              <motion.div
                key={restaurant.id}
                whileHover={{ scale: 1.02 }}
                className={`bg-white/5 rounded-xl p-4 border cursor-pointer transition-all ${
                  selectedRestaurant?.id === restaurant.id
                    ? 'border-orange-500 bg-orange-500/10'
                    : 'border-white/10 hover:border-white/20'
                }`}
                onClick={() => setSelectedRestaurant(restaurant)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-white font-bold">{restaurant.name}</h3>
                    {restaurant.cuisine_type && (
                      <p className="text-gray-400 text-sm">{restaurant.cuisine_type}</p>
                    )}
                    {restaurant.address && (
                      <p className="text-gray-500 text-xs mt-1">{restaurant.address}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditingRestaurant(restaurant)
                        setRestaurantForm(restaurant)
                        setShowRestaurantForm(true)
                      }}
                      className="text-blue-400 hover:text-blue-300 p-1"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteRestaurant(restaurant.id)
                      }}
                      className="text-red-400 hover:text-red-300 p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="text-gray-400 text-xs line-clamp-2">{restaurant.description}</p>
              </motion.div>
            ))}
          </div>
          
          {restaurants.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <Store size={48} className="mx-auto mb-2 opacity-50" />
              <p>No restaurants yet. Click "Add Restaurant" to get started!</p>
            </div>
          )}
        </motion.div>

        {selectedRestaurant && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <UtensilsCrossed size={28} className="text-green-500" />
                Menu for {selectedRestaurant.name}
              </h2>
            </div>

            <div className="bg-white/5 rounded-xl p-6 mb-6 border border-white/10">
              <h3 className="text-white font-semibold mb-4 text-lg">
                {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Item Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Margherita Pizza"
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <DollarSign size={16} className="text-green-500" />
                    Price *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g., 12.99"
                    value={itemForm.price}
                    onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <Tag size={16} className="text-blue-500" />
                    Category
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Burgers, Pizza"
                    value={itemForm.category_name}
                    onChange={(e) => setItemForm({ ...itemForm, category_name: e.target.value })}
                    list="categories"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  />
                  <datalist id="categories">
                    {categories.map((cat, idx) => (
                      <option key={idx} value={cat.name} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <Clock size={16} className="text-yellow-500" />
                    Prep Time (minutes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="e.g., 15"
                    value={itemForm.preparation_time}
                    onChange={(e) => setItemForm({ ...itemForm, preparation_time: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-semibold mb-2">Description</label>
                  <textarea
                    placeholder="Describe this delicious item..."
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                    rows="2"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2">
                    <ImageIcon size={16} className="text-pink-500" />
                    Image URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/item-image.jpg"
                    value={itemForm.image_url}
                    onChange={(e) => setItemForm({ ...itemForm, image_url: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-2">Allergens</label>
                  <input
                    type="text"
                    placeholder="e.g., Nuts, Dairy, Gluten (comma-separated)"
                    value={itemForm.allergens}
                    onChange={(e) => setItemForm({ ...itemForm, allergens: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white text-sm font-semibold mb-2">Dietary Tags</label>
                  <input
                    type="text"
                    placeholder="e.g., Vegan, Vegetarian, Halal (comma-separated)"
                    value={itemForm.dietary_tags}
                    onChange={(e) => setItemForm({ ...itemForm, dietary_tags: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div className="md:col-span-2 flex gap-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={itemForm.is_available}
                      onChange={(e) => setItemForm({ ...itemForm, is_available: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-600 text-green-500 focus:ring-green-500"
                    />
                    <span className="text-white font-semibold">Available for ordering</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={itemForm.is_featured}
                      onChange={(e) => setItemForm({ ...itemForm, is_featured: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-white font-semibold">Featured item</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleItemSubmit}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
                >
                  <Plus size={18} />
                  {loading ? 'Saving...' : editingItem ? 'Update Item' : 'Add Item'}
                </button>
                {editingItem && (
                  <button
                    onClick={() => {
                      setEditingItem(null)
                      setItemForm({
                        name: '',
                        description: '',
                        price: '',
                        category_name: '',
                        image_url: '',
                        preparation_time: '',
                        allergens: '',
                        dietary_tags: '',
                        is_available: true,
                        is_featured: false
                      })
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-orange-500 border-t-transparent"></div>
              </div>
            ) : menuItems.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <UtensilsCrossed size={48} className="mx-auto mb-2 opacity-50" />
                <p>No menu items yet. Add your first item above!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {menuItems.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="text-white font-semibold">{item.name}</h4>
                        {item.category_name && (
                          <span className="inline-block bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded mt-1">
                            {item.category_name}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => toggleAvailability(item.id, item.is_available)}
                        className={`p-1 ${item.is_available ? 'text-green-400' : 'text-gray-500'}`}
                      >
                        {item.is_available ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                    </div>
                    
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">{item.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-green-400 font-bold text-lg">${item.price}</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setEditingItem(item)
                            setItemForm({
                              name: item.name,
                              price: item.price,
                              description: item.description || '',
                              category_name: item.category_name || '',
                              image_url: item.image_url || '',
                              preparation_time: item.preparation_time || '',
                              allergens: item.allergens ? item.allergens.join(', ') : '',
                              dietary_tags: item.dietary_tags ? item.dietary_tags.join(', ') : '',
                              is_available: item.is_available,
                              is_featured: item.is_featured || false
                            })
                          }}
                          className="text-blue-400 hover:text-blue-300 p-1"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}
